import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Chip,
  Link as MuiLink,
  Button,
  Paper,
  Card,
  CardContent,
  Divider,
  Container,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SpeedIcon from "@mui/icons-material/Speed";
import SavingsIcon from "@mui/icons-material/Savings";
import SearchIcon from "@mui/icons-material/Search";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getImageUrl } from "../../../utils/helpers";

const AiPatentAnalysis = ({ baseConfig }) => {
  const { priColor, priHoverColor, tertColor, tertHoverColor } =
    baseConfig.siteBranding;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const technologies = [
    "Targeted Prosecution",
    "Licensing Target Search",
    "Patent Acquisition Search",
    "Clearance Search",
    "Technology Landscape Study",
  ];

  const aiCapabilities = [
    "AI Model Training",
    "Reinforcement Learning",
    "AI Output Evaluation",
  ];

  const benefits = [
    {
      icon: <SpeedIcon />,
      title: "Enhanced Efficiency",
      description:
        "Our AI-powered patent analysis tools can process thousands of patents in minutes, dramatically reducing research time.",
    },
    {
      icon: <SavingsIcon />,
      title: "Cost Effectiveness",
      description:
        "Automated analysis reduces billable hours while maintaining high accuracy through attorney oversight.",
    },
    {
      icon: <SearchIcon />,
      title: "Comprehensive Coverage",
      description:
        "AI tools can identify relevant patents that might be missed by traditional keyword-based searches.",
    },
    {
      icon: <AutoGraphIcon />,
      title: "Strategic Insights",
      description:
        "Advanced analytics provide valuable competitive intelligence and landscape mapping for informed decision-making.",
    },
  ];

  const SectionHeading = ({ children, icon, mb = 3 }) => (
    <Box sx={{ display: "flex", alignItems: "center", mb }}>
      {icon && (
        <Box sx={{ color: tertColor, mr: 1.5, display: "flex" }}>{icon}</Box>
      )}
      <Typography variant="h6" fontWeight="bold" color={tertColor}>
        {children}
      </Typography>
    </Box>
  );

  const TechChip = ({ label }) => (
    <Chip
      label={label}
      sx={{
        m: 0.5,
        backgroundColor: `${tertColor}11`,
        borderColor: tertColor,
        borderWidth: 1,
        color: "text.primary",
        fontWeight: 500,
        "&:hover": { backgroundColor: `${tertColor}22` },
      }}
      variant="outlined"
    />
  );

  const ActionButton = ({
    to,
    startIcon,
    children,
    colorType = "tert",
    mr = 0,
    fullWidth = false,
  }) => (
    <Button
      startIcon={startIcon}
      variant="contained"
      component={RouterLink}
      to={to}
      fullWidth={fullWidth}
      sx={{
        mr,
        py: 1,
        borderRadius: 1.5,
        textTransform: "none",
        fontWeight: 600,
        backgroundColor: colorType === "pri" ? priColor : tertColor,
        color: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        "&:hover": {
          backgroundColor: colorType === "pri" ? priHoverColor : tertHoverColor,
          color: "#fff",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          transform: "translateY(-2px)",
        },
        transition: "all 0.2s ease-in-out",
      }}
    >
      {children}
    </Button>
  );

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
        background: "#fff",
        mb: 6,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 2.5 },
          display: "flex",
          alignItems: "center",
          background: `linear-gradient(120deg, ${tertColor}22, ${tertColor}44)`,
          borderBottom: `1px solid ${tertColor}33`,
        }}
      >
        <SmartToyIcon
          sx={{ mr: 2, color: tertColor, fontSize: { xs: 28, md: 32 } }}
        />
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ fontSize: { xs: "1.25rem", md: "1.5rem" } }}
        >
          AI in Patent Analysis
        </Typography>
      </Box>

      {/* Main Content */}
      <Paper elevation={0} sx={{ background: "#fff", p: { xs: 2.5, md: 4 } }}>
        {/* Top Section: Image Left, Text Right */}
        <Grid
          container
          spacing={isMobile ? 4 : 6}
          alignItems="center"
          sx={{ mb: 6 }}
        >
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={getImageUrl("kth-ai-in-patent-analysis.png")}
              alt="AI in Patent Analysis"
              sx={{
                width: "100%",
                borderRadius: 2,
                boxShadow: 2,
                transform: "scale(1.02)",
                transition: "transform 0.3s ease",
                "&:hover": { transform: "scale(1.03)" },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionHeading mb={2}>
              Using AI for Enhanced Patent Analysis
            </SectionHeading>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 2, lineHeight: 1.6 }}
            >
              Kama Thuo, PLLC leverages AI technologies to enhance the
              efficiency and effectiveness of patent analysis, offering services
              like claim amendment studies for targeted prosecution, competitive
              landscape analysis, licensing target identification, patent
              portfolio optimization, and technology scouting.
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 3, lineHeight: 1.6 }}
            >
              Our AI-enhanced approach combines technological automation with
              expert attorney oversight, ensuring both efficiency and accuracy
              in patent analysis. This hybrid methodology delivers faster
              results without sacrificing the critical human judgment essential
              in legal matters.
            </Typography>

            <ActionButton
              to="/patents"
              startIcon={<LaunchIcon />}
              colorType="tert"
              fullWidth={isMobile}
            >
              Learn More About Our Patent Services
            </ActionButton>
          </Grid>
        </Grid>

        {/* Benefits Section: Full Width */}
        <Box sx={{ mb: 6 }}>
          <SectionHeading mb={2.5}>
            Key Benefits of AI-Powered Patent Analysis
          </SectionHeading>
          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 2,
                    border: `1px solid ${tertColor}22`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                      borderColor: `${tertColor}44`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box display="flex" alignItems="center" mb={1.5}>
                      <Box sx={{ color: tertColor, mr: 1.5 }}>
                        {benefit.icon}
                      </Box>
                      <Typography variant="h6" fontWeight={600} fontSize="1rem">
                        {benefit.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", lineHeight: 1.6 }}
                    >
                      {benefit.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Technologies Section: Full Width */}
        <Box sx={{ mb: 6 }}>
          <SectionHeading mb={1.5}>Technologies We Analyze</SectionHeading>
          <Box sx={{ display: "flex", flexWrap: "wrap" }}>
            {technologies.map((tech, index) => (
              <TechChip key={index} label={tech} />
            ))}
          </Box>
        </Box>

        <Divider sx={{ my: { xs: 4, md: 5 }, borderColor: `${tertColor}22` }} />

        {/* Final Section */}
        <Grid container spacing={isMobile ? 4 : 6} alignItems="stretch">
          <Grid item xs={12} md={8}>
            <SectionHeading icon={<SmartToyIcon />} mb={3}>
              Developing and Evaluating AI Applications for Patent Analysis
            </SectionHeading>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 2.5, lineHeight: 1.6 }}
            >
              In collaboration with our AI Automation vendor{" "}
              <MuiLink
                href="https://ai.rfwel.com/"
                target="_blank"
                sx={{
                  color: tertColor,
                  fontWeight: 500,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Rfwel Engineering
              </MuiLink>
              , we assist clients in evaluating AI applications for accuracy,
              and train/fine-tune models based on legal analysis of generated
              outputs.
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 3, lineHeight: 1.6 }}
            >
              Our specialized expertise bridges the gap between patent law and
              AI technology, enabling us to develop tailored AI solutions for
              patent professionals. Whether you're looking to implement AI in
              your patent practice or need assistance evaluating existing AI
              patent tools, our team provides technically informed legal
              guidance.
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", mb: 3 }}>
              {aiCapabilities.map((capability, index) => (
                <TechChip key={index} label={capability} />
              ))}
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <ActionButton
                to="/news-insights?category=ai-training-fine-tuning"
                startIcon={<LaunchIcon />}
                colorType="pri"
                fullWidth={isMobile}
              >
                Learn More: Training
              </ActionButton>
              <ActionButton
                to="/news-insights?category=ai-evaluation"
                startIcon={<LaunchIcon />}
                colorType="pri"
                fullWidth={isMobile}
              >
                Learn More: Evaluation
              </ActionButton>
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={1}
              sx={{
                p: 3,
                height: "100%",
                backgroundColor: "#f9f9f9",
                borderRadius: 2,
                border: `1px solid ${tertColor}22`,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                fontWeight="bold"
                color={tertColor}
                sx={{ mb: 2.5 }}
              >
                Our AI Patent Services Include:
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {[
                  "Supporting development of custom AI models & tools for patent analysis",
                  "Legal review of AI-generated patent analytics",
                  "Training data preparation for patent AI systems",
                  "Legal evaluation of third-party AI patent tools",
                  "Strategic implementation of AI in patent processes",
                ].map((service, index) => (
                  <Box
                    key={index}
                    sx={{ display: "flex", alignItems: "flex-start" }}
                  >
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.25, mr: 1 }}
                      fontSize="small"
                    />
                    <Typography
                      variant="body2"
                      sx={{ lineHeight: 1.5, fontWeight: 500 }}
                    >
                      {service}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default AiPatentAnalysis;
